import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth/auth-provider';
import type { ConfirmError, ResendError, SignUpError } from '@/lib/auth/errors';
import { updateMyProfile, type ProfileUpdate } from '@/lib/api/me';

import { ConfirmCodeStep } from './ConfirmCodeStep';
import { ProfileRetryStep } from './ProfileRetryStep';
import { SignUpFormStep, type SignUpFormValues } from './SignUpFormStep';
import { SignUpMarketing } from './SignUpMarketing';

interface SignUpDialogProps {
  /**
   * Which step the dialog opens on. `/signup` starts at `form`; `/confirm`
   * starts at `code` and skips the credentials capture (the user already
   * has an unconfirmed Cognito account from an earlier session).
   */
  readonly initialStep: 'form' | 'code';
  /**
   * Email shown on the code step. Required when `initialStep === 'code'`;
   * carried over automatically from the form step otherwise.
   */
  readonly initialEmail?: string;
  /**
   * What to do when the user successfully completes the wizard.
   * `/signup` auto-signs-in + persists the profile + routes to `/app`;
   * `/confirm` routes to `/signin` (we don't have the user's password
   * from an earlier session — and no form-captured profile fields).
   */
  readonly onComplete: () => void | Promise<void>;
  /**
   * When true, renders a banner above the form explaining that the
   * user arrived via a BigCommerce install and the connection will
   * be attached once they finish signing up. Set by `SignUpPage` when
   * `?bc_claim=…` is on the URL (ADR 0011's BC-initiated flow).
   */
  readonly bcClaimNotice?: boolean;
}

/**
 * Multi-step sign-up wizard. Wrapped in a shadcn Dialog so the modal
 * lifecycle is handled (Escape, outside click, focus trap). Non-
 * dismissible — the only way out is to complete sign-up or click the
 * "Sign in" link inside the form.
 *
 * State machine (the `/signup` path):
 *   form → code (after Cognito SignUp succeeds)
 *   code → ConfirmCodeStep handles OTP entry; on submit:
 *     1. confirmSignUp (skipped if `codeConfirmed`)
 *     2. signIn (skipped if `signedIn`)
 *     3. updateMyProfile (skipped if no form profile to save)
 *     → onComplete
 *   profile-save failure → ProfileRetryStep replaces ConfirmCodeStep;
 *     retry re-runs only step 3 (steps 1 + 2 stay "done")
 *
 * The `/confirm` path (initialStep=code, no password, no formProfile)
 * runs only step 1 and then onComplete — no auto-sign-in, no profile
 * save.
 */
export function SignUpDialog({
  initialStep,
  initialEmail = '',
  onComplete,
  bcClaimNotice = false,
}: SignUpDialogProps) {
  const navigate = useNavigate();
  const { signUp, confirmSignUp, resendConfirmationCode, signIn } = useAuth();

  const [step, setStep] = useState<'form' | 'code'>(initialStep);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [topError, setTopError] = useState<string | null>(null);

  // Per-step success flags — gates so retries don't re-run completed
  // work. confirmSignUp can't be safely re-run (the code is consumed);
  // signIn would just be redundant network if already-signed-in;
  // updateMyProfile is the only retryable step on its own.
  const [codeConfirmed, setCodeConfirmed] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  // Profile fields captured at form-step time. Held in memory until the
  // post-confirmation trigger has run (Cognito → DynamoDB), then sent
  // via PATCH /me. Null for the /confirm path (no form, no profile).
  const [formProfile, setFormProfile] = useState<ProfileUpdate | null>(null);

  // If non-null: the profile-save step failed. Render ProfileRetryStep
  // instead of ConfirmCodeStep until retry succeeds.
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null);

  const handleFormSubmit = async (values: SignUpFormValues): Promise<void> => {
    setTopError(null);
    const result = await signUp(values.email, values.password);

    if (result.isErr()) {
      setTopError(signUpErrorMessage(result.error));
      return;
    }
    setEmail(values.email);
    setPassword(values.password);
    setFormProfile({
      name: values.name,
      country: values.country,
      marketing_opt_in: values.marketingOptIn,
    });
    setStep('code');
  };

  // The actual save call — extracted so the OTP-submit path and the
  // retry-button path can share it.
  const saveProfile = async (): Promise<boolean> => {
    if (!formProfile) return true;
    const result = await updateMyProfile(formProfile);
    if (result.isErr()) {
      const message =
        result.error.kind === 'unauthorised'
          ? 'Your session expired. Please sign in again.'
          : "We couldn't save your profile details. Please try again.";
      toast.error(message);
      setProfileSaveError(message);
      return false;
    }
    setProfileSaveError(null);
    return true;
  };

  const handleCodeSubmit = async (code: string): Promise<void> => {
    setTopError(null);

    if (!codeConfirmed) {
      const confirmResult = await confirmSignUp(email, code);
      if (confirmResult.isErr()) {
        setTopError(confirmErrorMessage(confirmResult.error));
        return;
      }
      setCodeConfirmed(true);
    }

    if (!signedIn && password) {
      const signInResult = await signIn(email, password);
      if (signInResult.isErr()) {
        setTopError(
          "We confirmed your account but couldn't sign you in automatically. Try signing in from the next screen.",
        );
        void navigate('/signin');
        return;
      }
      if (signInResult.value.kind !== 'authenticated') {
        // mfa-required can't actually happen on a brand-new account
        // (MFA isn't enrolled yet), but guard anyway.
        setTopError(
          "We confirmed your account but couldn't complete sign-in. Try signing in from the next screen.",
        );
        void navigate('/signin');
        return;
      }
      setSignedIn(true);
    }

    const saved = await saveProfile();
    if (!saved) return;

    await onComplete();
  };

  const handleProfileRetry = async (): Promise<void> => {
    const saved = await saveProfile();
    if (saved) {
      await onComplete();
    }
  };

  const handleResend = async (): Promise<void> => {
    setTopError(null);
    const result = await resendConfirmationCode(email);
    if (result.isErr()) {
      setTopError(resendErrorMessage(result.error));
    }
  };

  return (
    <Dialog open>
      <DialogContent
        // shadcn's DialogContent ships with `sm:max-w-lg` (512px) baked in.
        // We need the responsive variant on our override too, otherwise it
        // loses to the more-specific default at the `sm:` breakpoint and
        // the modal stays narrow. ~1024px matches Stripe's signup width.
        className="w-full gap-0 overflow-hidden p-0 sm:max-w-5xl sm:rounded-lg"
        // Non-dismissible: no X, no Escape, no outside-click. Sign-up
        // is a deliberate commitment.
        showCloseButton={false}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="space-y-6 border-b p-6 sm:border-b-0 sm:border-r sm:p-8">
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-foreground text-xl">
                {dialogTitle(step, profileSaveError)}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                {dialogDescription(step, profileSaveError)}
              </DialogDescription>
            </DialogHeader>

            {bcClaimNotice ? (
              <div className="border-primary/40 bg-primary/5 text-foreground rounded-md border p-3 text-sm">
                <p className="font-medium">Linking your BigCommerce store</p>
                <p className="text-muted-foreground mt-1">
                  We&apos;ll attach your store to your Mailchute account once you finish signing up.
                </p>
              </div>
            ) : null}

            {step === 'form' ? (
              <SignUpFormStep onSubmit={handleFormSubmit} topError={topError} />
            ) : profileSaveError ? (
              <ProfileRetryStep errorMessage={profileSaveError} onRetry={handleProfileRetry} />
            ) : (
              <ConfirmCodeStep
                email={email}
                onSubmit={handleCodeSubmit}
                onResend={handleResend}
                topError={topError}
              />
            )}
          </div>

          <div className="bg-muted/30 p-6 sm:p-8">
            <SignUpMarketing />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function dialogTitle(step: 'form' | 'code', profileSaveError: string | null): string {
  if (step === 'form') return 'Create your Mailchute.ai account';
  if (profileSaveError) return 'Almost there';
  return 'Confirm your email';
}

function dialogDescription(step: 'form' | 'code', profileSaveError: string | null): string {
  if (step === 'form') return 'Get started with shipping software you can talk to.';
  if (profileSaveError) return 'Your account is created — one more step to finish.';
  return 'One last step before we can sign you in.';
}

function signUpErrorMessage(error: SignUpError): string {
  switch (error.kind) {
    case 'email-already-in-use':
      return 'That email already has a Mailchute.ai account. Try signing in instead.';
    case 'invalid-password':
      return error.message;
    case 'invalid-email':
      return 'That email address looks invalid. Please double-check and try again.';
    case 'too-many-requests':
      return 'Too many attempts. Please try again in a few minutes.';
    case 'unknown':
      return error.message;
  }
}

function confirmErrorMessage(error: ConfirmError): string {
  switch (error.kind) {
    case 'code-mismatch':
      return 'That code is incorrect. Double-check the email and try again.';
    case 'code-expired':
      return 'That code has expired. Click "Resend code" for a new one.';
    case 'invalid-code-format':
      return 'Verification codes are six digits. Check what you entered.';
    case 'too-many-requests':
      return 'Too many attempts. Please wait a minute before trying again.';
    case 'user-not-found':
      return 'No pending account for this email. Try signing up again.';
    case 'unknown':
      return error.message;
  }
}

function resendErrorMessage(error: ResendError): string {
  switch (error.kind) {
    case 'too-many-requests':
      return 'You can only resend a code every 30 seconds. Try again shortly.';
    case 'user-not-found':
      return 'No pending account for this email. Try signing up again.';
    case 'already-confirmed':
      return 'This account is already confirmed. You can sign in.';
    case 'unknown':
      return error.message;
  }
}
